import AudienceTemplateModalType from '@/Enums/AudienceTemplates/ModalType.enum';
import AudienceTemplateModalVariant from '@/Enums/AudienceTemplates/ModalVariant.enum';
import { Channel } from '@/Enums/Channel.enum';
import type {
  ForecasterAudienceBlocks,
  IForecasterAudienceBlock,
} from '@/Interfaces/AudienceTemplates/AudienceBlock.interface';
import type { IBaseAudienceTargeting } from '@/Interfaces/Audiences/Audience.interface';
import type { IAudienceControlOption } from '@/Interfaces/ControlOption.interface';
import AudienceTargetingService from '@/Services/AudienceTargeting.service';
import AudienceHeader from '@/components/AudienceHeader';
import AudienceListContainer from '@/components/AudienceList';
import AudienceBlockList from '@/components/AudienceTemplates/AudienceBlockList';
import AudienceTemplatesModal from '@/components/AudienceTemplates/AudienceTemplatesModal';
import SupportRequestModal from '@/components/Modal/SupportRequestModal';
import { downloadTableCsv } from '@/helpers/downloadCSV';
import { useAppSelector } from '@/store/hooks';
import { selectExtendedAudiences } from '@/store/selectors/currentWhitelabel.selectors';
import { selectForecasterAudiences } from '@/store/selectors/forecaster.selectors';
import { selectIsAdmin } from '@/store/selectors/me.selectors';
import { Paper } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface IProps {
  currentChannel: Channel;
}

const Audience = ({ currentChannel }: IProps) => {
  const { setValue, control } = useFormContext();
  const audience = useAppSelector(selectForecasterAudiences);
  const extendedAudiences = useAppSelector(selectExtendedAudiences);
  const isAdmin = useAppSelector(selectIsAdmin);

  const [isRequestAudiencesModalOpen, setIsRequestAudiencesModalOpen] = useState<boolean>(false);
  const [templatesModal, setTemplatesModal] = useState<{ open: boolean; type: AudienceTemplateModalType }>({
    open: false,
    type: AudienceTemplateModalType.View,
  });

  const openTemplates = useCallback((type: AudienceTemplateModalType) => setTemplatesModal({ open: true, type }), []);

  const closeTemplates = useCallback(() => setTemplatesModal(state => ({ ...state, open: false })), []);

  const options = useMemo(() => audience[currentChannel] ?? [], [audience, currentChannel]);

  // Templates store numeric Beeswax audience IDs only; Adswizz/Audio channels use string IDs (excluded in v1).
  const templatesEnabled = currentChannel !== Channel.Audio && currentChannel !== Channel.SpotifyDirect;

  const templateOptions = useMemo(() => options as IAudienceControlOption<number>[], [options]);

  const currentAudienceControlName = useMemo(() => {
    if (currentChannel === Channel.SpotifyDirect) return `${currentChannel}.selectedAdswizzAudience`;
    return `${currentChannel}.selectedAudience`;
  }, [currentChannel]);

  const currentAudience = useWatch({ control, name: currentAudienceControlName });

  const onAudienceChange = useCallback(
    (newAudience: IBaseAudienceTargeting<IAudienceControlOption<string | number>>) => {
      setValue(currentAudienceControlName, newAudience);
    },
    [currentAudienceControlName, setValue],
  );

  const audienceBlocksName = useMemo(() => `${currentChannel}.selectedAudienceBlocks`, [currentChannel]);
  const audienceBlocks = (useWatch({ control, name: audienceBlocksName }) ?? []) as ForecasterAudienceBlocks;
  const inBlockMode = audienceBlocks.length > 0;

  const setBlocks = useCallback(
    (next: ForecasterAudienceBlocks) => setValue(audienceBlocksName, next, { shouldDirty: true }),
    [audienceBlocksName, setValue],
  );

  const onApplyBlock = useCallback(
    (block: IForecasterAudienceBlock) => {
      if (AudienceTargetingService.checkAudiencesInTargeting(currentAudience) && !inBlockMode) {
        if (!window.confirm('Applying a template will clear your current segment selection. Continue?')) {
          return;
        }
        setValue(currentAudienceControlName, AudienceTargetingService.defaultAudienceTargeting);
      }
      const grid = audienceBlocks;
      const next = grid.length ? grid.map(row => [...row]) : [[]];
      next[next.length - 1].push(block);
      setBlocks(next);
    },
    [audienceBlocks, currentAudience, currentAudienceControlName, inBlockMode, setBlocks, setValue],
  );

  const onAddAndRow = useCallback(() => {
    const last = audienceBlocks[audienceBlocks.length - 1];
    if (!audienceBlocks.length || !last?.length) {
      return;
    }
    setBlocks([...audienceBlocks, []]);
  }, [audienceBlocks, setBlocks]);

  const canAddAndRow = inBlockMode && !!audienceBlocks[audienceBlocks.length - 1]?.length;

  const onRemoveBlock = useCallback(
    (rowIndex: number, blockIndex: number) => {
      const next = audienceBlocks
        .map((row, r) => (r === rowIndex ? row.filter((_, b) => b !== blockIndex) : row))
        .filter(row => row.length);
      setBlocks(next);
    },
    [audienceBlocks, setBlocks],
  );

  const onApplyTemplate = useCallback(
    (value: IBaseAudienceTargeting<IAudienceControlOption<number>>) => onAudienceChange(value),
    [onAudienceChange],
  );

  const onResetAudience = useCallback(() => {
    onAudienceChange(AudienceTargetingService.defaultAudienceTargeting);
    setBlocks([]);
  }, [onAudienceChange, setBlocks]);

  const onRequestAudiences = useCallback(() => {
    setIsRequestAudiencesModalOpen(true);
  }, []);

  const onDownload = useCallback(() => {
    const currentChannelAudience = audience[currentChannel] ?? [];
    const formattedData = currentChannelAudience.map(audience => ({
      Audience: audience.label,
    }));
    downloadTableCsv(formattedData, 'Audience Selection');
  }, [currentChannel, audience]);

  return (
    <Paper className='flex justify-start items-start rounded-lg flex-col'>
      <AudienceHeader
        onResetAudience={onResetAudience}
        onRequestAudiences={onRequestAudiences}
        onDownload={onDownload}
        downloadButtonDisabled={false}
        templatesEnabled={templatesEnabled}
        onCreateTemplate={isAdmin ? () => openTemplates(AudienceTemplateModalType.Create) : undefined}
        onViewTemplates={() => openTemplates(AudienceTemplateModalType.View)}
      />
      {inBlockMode ? (
        <div className='w-full p-[15px]'>
          <AudienceBlockList value={audienceBlocks} onRemoveBlock={onRemoveBlock} />
        </div>
      ) : (
        <AudienceListContainer
          allowExtendedGrouping={extendedAudiences}
          onChange={onAudienceChange}
          options={options}
          value={currentAudience}
          containerSx={{ padding: '15px' }}
        />
      )}
      <SupportRequestModal
        title='Audiences'
        isOpen={isRequestAudiencesModalOpen}
        handleClose={() => setIsRequestAudiencesModalOpen(false)}
      />
      {templatesEnabled && (
        <AudienceTemplatesModal
          open={templatesModal.open}
          initialType={templatesModal.type}
          options={templateOptions}
          currentValue={
            (currentAudience ?? AudienceTargetingService.defaultAudienceTargeting) as IBaseAudienceTargeting<
              IAudienceControlOption<number>
            >
          }
          allowExtendedGrouping={extendedAudiences}
          onClose={closeTemplates}
          onApply={onApplyTemplate}
          variant={AudienceTemplateModalVariant.Blocks}
          onApplyBlock={onApplyBlock}
          onAddAndRow={onAddAndRow}
          canAddAndRow={canAddAndRow}
        />
      )}
    </Paper>
  );
};

export default Audience;
